import { useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";

import useLogout from "./useLogout";

interface UseSessionTimeoutOptions {
  timeout: number; // in milliseconds
  onTimeout?: () => void;
}

const useSessionTimeout = ({
  timeout,
  onTimeout,
}: UseSessionTimeoutOptions) => {
  const navigate = useNavigate();
  const handleLogout = useLogout();
  const lang = localStorage.getItem("lang") || "en";

  const resetTimeout = useCallback(() => {
    let timeoutId: number;

    const handleTimeout = async () => {
      if (onTimeout) {
        onTimeout();
      }
      await handleLogout();
      navigate(`/${lang}/auth/login`);
    };

    const clearExistingTimeout = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };

    const setNewTimeout = () => {
      clearExistingTimeout();
      timeoutId = window.setTimeout(handleTimeout, timeout);
    };

    // Set initial timeout
    setNewTimeout();

    // Add event listeners to reset timeout on user activity
    const events = ["mousedown", "keydown", "scroll", "touchstart"];
    events.forEach((event) => {
      window.addEventListener(event, setNewTimeout);
    });

    return () => {
      clearExistingTimeout();
      events.forEach((event) => {
        window.removeEventListener(event, setNewTimeout);
      });
    };
  }, [onTimeout, handleLogout, navigate, lang, timeout]);

  useEffect(() => {
    const cleanup = resetTimeout();
    return cleanup;
  }, [resetTimeout]);
};

export default useSessionTimeout;
